import { type ApiSchemas, call } from '@rental/contracts/client'
import { format, formatDate, formatEuro } from '@rental/i18n'
import { Button, Card, Field } from '@rental/ui'
import { useQueryClient } from '@tanstack/react-query'
import { type FormEvent, useRef, useState } from 'react'
import { can } from '../access'
import { useAccess } from '../access-context'
import { api } from '../api'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { OutcomeNotice } from '../components/OutcomeNotice'
import { ProblemNotice, ScrollRegion, StaffInput, StaffSelect } from '../components/ui'
import { useSend } from '../fines/send'
import { useLocale } from '../i18n'
import type { StaffProblem } from '../problems'
import { romeNow } from '../time'
import {
  type DraftField,
  emptyDraft,
  type InterventionDraft,
  interventionBody,
  MAINTENANCE_KINDS,
  MAINTENANCE_QUERY_KEY,
} from './maintenance'

export type Vehicle = Pick<ApiSchemas['VehicleRow'], 'id' | 'plate' | 'make' | 'model'>

export const vehicleLabel = (vehicle: Vehicle) =>
  `${vehicle.plate} · ${vehicle.make} ${vehicle.model}`

/** A reason of at least this many letters: the fines' rule, for every record removed with one. */
const REASON_MIN = 3

/**
 * The register's rows, or that there are none. The empty state is drawn here, not by the
 * caller, so this stays mounted when the last row is voided: the outcome said above the
 * table must outlive the table (the E2E found it gone with the row).
 */
export function InterventionTable({ items }: { items: readonly ApiSchemas['Intervention'][] }) {
  const { locale, messages: m } = useLocale()
  const { access } = useAccess()
  const columns = m.maintenance.columns
  const head = 'py-1 pr-4'
  // OPS-1: who may manage the fleet voids an intervention from its row; the row then leaves the
  // list (voided ones are out of lists and totals), so the outcome is said above the table.
  const manage = can(access, 'fleet.manage')
  const [voided, setVoided] = useState<string | null>(null)
  return (
    <>
      {voided ? (
        <OutcomeNotice key={voided}>
          <p>{m.maintenance.void.done}</p>
        </OutcomeNotice>
      ) : null}
      {items.length === 0 ? (
        <p className="text-text-muted">{m.maintenance.empty}</p>
      ) : (
        <ScrollRegion label={m.maintenance.title}>
          <table className="w-full text-left text-sm">
            <thead>
              <tr>
                <th scope="col" className={head}>
                  {columns.date}
                </th>
                <th scope="col" className={head}>
                  {columns.vehicle}
                </th>
                <th scope="col" className={head}>
                  {columns.kind}
                </th>
                <th scope="col" className={head}>
                  {columns.description}
                </th>
                <th scope="col" className={`${head} text-right`}>
                  {columns.cost}
                </th>
                <th scope="col" className={head}>
                  {columns.supplier}
                </th>
                <th scope="col" className={manage ? head : 'py-1'}>
                  {columns.rental}
                </th>
                {manage ? (
                  <th scope="col" className="py-1">
                    <span className="sr-only">{m.maintenance.void.action}</span>
                  </th>
                ) : null}
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id} className="border-t border-border align-top">
                  <td className="py-1 pr-4">
                    {item.performedOn ? formatDate(locale, item.performedOn, 'short') : '—'}
                  </td>
                  <td className="py-1 pr-4">{item.vehicleLabel}</td>
                  <td className="py-1 pr-4">
                    {item.kind === 'block' ? '—' : m.maintenance.kinds[item.kind]}
                  </td>
                  <td className="py-1 pr-4 break-words">{item.description}</td>
                  <td className="figures py-1 pr-4 text-right">
                    {item.costCents === null ? '—' : formatEuro(item.costCents, locale)}
                  </td>
                  <td className="py-1 pr-4">{item.supplier ?? '—'}</td>
                  <td className={manage ? 'py-1 pr-4' : 'py-1'}>{item.rentalId ?? '—'}</td>
                  {manage ? (
                    <td className="py-1">
                      {item.voided ? null : (
                        <VoidAction item={item} onVoided={() => setVoided(item.id)} />
                      )}
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </ScrollRegion>
      )}
    </>
  )
}

/**
 * OPS-1: voids an intervention with a reason, after a question that names it (D11, widened
 * to removing a record). The row stays in the database, struck out of lists and totals; the
 * reason goes on the audit row.
 */
function VoidAction({
  item,
  onVoided,
}: {
  item: ApiSchemas['Intervention']
  onVoided: () => void
}) {
  const { locale, messages: m } = useLocale()
  const copy = m.maintenance.void
  const queryClient = useQueryClient()
  const dialog = useRef<HTMLDialogElement>(null)
  const [reason, setReason] = useState('')
  const { send, busy, problem, clear } = useSend()
  const text = reason.trim()
  const short = text.length > 0 && text.length < REASON_MIN
  const ready = text.length >= REASON_MIN

  async function confirm() {
    const answer = await send(() =>
      call(
        api.POST('/v1/staff/interventions/{interventionId}/void', {
          params: { path: { interventionId: item.id } },
          body: { reason: text },
        }),
      ),
    )
    if (!answer) return
    dialog.current?.close()
    // Said before the row goes, so the focus has somewhere to land (UI-2g).
    onVoided()
    await queryClient.invalidateQueries({ queryKey: MAINTENANCE_QUERY_KEY })
  }

  return (
    <>
      <Button
        variant="outline"
        className="rounded-sm"
        aria-label={format(copy.label, { description: item.description })}
        onClick={() => {
          setReason('')
          clear()
          dialog.current?.showModal()
        }}
      >
        {copy.action}
      </Button>
      <ConfirmDialog
        dialogRef={dialog}
        title={format(copy.title, {
          date: item.performedOn ? formatDate(locale, item.performedOn, 'short') : '—',
        })}
        confirmLabel={copy.confirmAction}
        cancelLabel={copy.keep}
        busy={busy}
        ready={ready}
        reasons={[format(copy.why, { min: REASON_MIN })]}
        onConfirm={() => void confirm()}
      >
        <p>{format(copy.details, { vehicle: item.vehicleLabel, description: item.description })}</p>
        <p>{copy.confirm}</p>
        <Field
          label={copy.reason}
          required
          error={short ? format(copy.why, { min: REASON_MIN }) : null}
        >
          {(control) => (
            <StaffInput
              {...control}
              maxLength={500}
              autoComplete="off"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
          )}
        </Field>
        <ProblemNotice problem={problem} />
      </ConfirmDialog>
    </>
  )
}

/** The create form; `vehicleId` pre-fills (and the caller may narrow `fleet` to that vehicle). */
export function NewIntervention({
  fleet,
  vehicleId = '',
  onClose,
}: {
  fleet: readonly Vehicle[]
  vehicleId?: string
  onClose: () => void
}) {
  const { messages: m } = useLocale()
  const copy = m.maintenance.form
  const queryClient = useQueryClient()
  const [draft, setDraft] = useState<InterventionDraft>(() => emptyDraft(romeNow().date, vehicleId))
  const [invalid, setInvalid] = useState<readonly DraftField[]>([])
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<StaffProblem | null>(null)
  const change = (patch: Partial<InterventionDraft>) => setDraft({ ...draft, ...patch })

  async function submit(event: FormEvent) {
    event.preventDefault()
    const outcome = interventionBody(draft)
    if (!outcome.ok) {
      setInvalid(outcome.invalid)
      return
    }
    setInvalid([])
    setProblem(null)
    setBusy(true)
    const result = await call(api.POST('/v1/staff/interventions', { body: outcome.body }))
    setBusy(false)
    if (!result.ok) {
      setProblem(result.problem)
      return
    }
    await queryClient.invalidateQueries({ queryKey: MAINTENANCE_QUERY_KEY })
    onClose()
  }

  const bad = (field: DraftField) => invalid.includes(field)
  return (
    <Card>
      <form onSubmit={(event) => void submit(event)} className="flex flex-col gap-3" noValidate>
        <h2 className="text-lg">{copy.title}</h2>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={copy.vehicle} required error={bad('vehicleId') ? copy.vehicle : null}>
            {(control) => (
              <StaffSelect
                {...control}
                value={draft.vehicleId}
                onChange={(event) => change({ vehicleId: event.target.value })}
              >
                <option value="" />
                {fleet.map((vehicle) => (
                  <option key={vehicle.id} value={vehicle.id}>
                    {vehicleLabel(vehicle)}
                  </option>
                ))}
              </StaffSelect>
            )}
          </Field>
          <Field label={copy.kind}>
            {(control) => (
              <StaffSelect
                {...control}
                value={draft.kind}
                onChange={(event) =>
                  change({ kind: event.target.value as InterventionDraft['kind'] })
                }
              >
                {MAINTENANCE_KINDS.map((kind) => (
                  <option key={kind} value={kind}>
                    {m.maintenance.kinds[kind]}
                  </option>
                ))}
              </StaffSelect>
            )}
          </Field>
          <Field label={copy.date} required error={bad('performedOn') ? copy.date : null}>
            {(control) => (
              <StaffInput
                {...control}
                type="date"
                value={draft.performedOn}
                onChange={(event) => change({ performedOn: event.target.value })}
              />
            )}
          </Field>
          <Field label={copy.cost} error={bad('cost') ? m.payment.amountInvalid : null}>
            {(control) => (
              <StaffInput
                {...control}
                inputMode="decimal"
                autoComplete="off"
                value={draft.cost}
                onChange={(event) => change({ cost: event.target.value })}
              />
            )}
          </Field>
          <Field
            label={copy.description}
            required
            error={bad('description') ? copy.description : null}
          >
            {(control) => (
              <StaffInput
                {...control}
                maxLength={500}
                autoComplete="off"
                value={draft.description}
                onChange={(event) => change({ description: event.target.value })}
              />
            )}
          </Field>
          <Field label={copy.supplier}>
            {(control) => (
              <StaffInput
                {...control}
                maxLength={200}
                autoComplete="off"
                value={draft.supplier}
                onChange={(event) => change({ supplier: event.target.value })}
              />
            )}
          </Field>
        </div>
        <ProblemNotice problem={problem} />
        <div className="flex flex-wrap gap-3">
          <Button type="submit" variant="staff" busy={busy}>
            {copy.save}
          </Button>
          <Button type="button" variant="outline" disabled={busy} onClick={onClose}>
            {copy.cancel}
          </Button>
        </div>
      </form>
    </Card>
  )
}
